import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FaCartPlus } from "react-icons/fa";
import { BsCartCheckFill } from "react-icons/bs";
import { FaHeart } from "react-icons/fa";
import { CiStar } from "react-icons/ci";
import {
  Button,
  Spinner,
} from "@chakra-ui/react";
import { FaArrowRightLong } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import {
  getProductPriceByUniceId,
} from "../../../redux/reduxApi/website/ProductReduxApi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { cartProductCount } from "../../../redux/reduxApi/website/CartProductCount";
import toast from "react-hot-toast";
import {
  addToWishlist,
  deleteFromWishlist,
} from "../../../redux/api/website/AddtoWishlistApi";
import {
  getWishlistCount,
  getWishlistProducts,
} from "../../../redux/reduxApi/website/WishlistApi";
import { IoSearch } from "react-icons/io5";
import { AddToCart } from "./utilsfuncart";
import {
  getEngineDataAPi,
  getMakeDataAPi,
  getModelDataAPi,
  getPartListDataAPi,
  getPartsByPartNo,
  getProductDataAPi,
  getYearDataAPi,
} from "../../../redux/reduxApi/website/MakeYearModel";
import { ChevronsRight, ChevronsLeft, X } from "lucide-react";
import Shoploader from "../loader/Shoploader";
import CheckboxSelect from "../utils/customComponent/CheckboxSelect";
import { addFilteredProducts } from "../../../redux/reduxSlice/website/ProductsSlice";
import { getDynamicProductList } from "../../../redux/api/admin/ProductApi";

// ProductCardSkeleton Component
const ProductCardSkeleton = () => (
  <div className="product-card flex border-2 min-h-10 shadow-lg rounded-md bg-white animate-pulse">
    <div className="lg:w-[20vw] md:w-[20vw] w-full">
      <div className="flex justify-between items-start p-2">
        <div className="flex space-x-1">
          <div className="h-6 w-12 bg-gray-300 rounded"></div>
          <div className="h-6 w-12 bg-gray-300 rounded"></div>
        </div>
        <div className="h-6 w-6 bg-gray-300 rounded-full"></div>
      </div>
      <div className="w-full lg:h-[25vh] md:h-[20vh] h-[26vh] bg-gray-300 rounded-t-md"></div>
      <div className="h-6 bg-gray-300 m-2 rounded"></div>
      <div className="flex items-center mt-1 y-2 mx-2">
        <div className="flex space-x-1 -mt-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-4 w-4 bg-gray-300 rounded"></div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between mx-2 mb-2 space-y-0">
        <div className="h-6 w-20 bg-gray-300 rounded"></div>
      </div>
    </div>
    <div className="lg:min-h-[35vh] p-2 overflow-hidden mt-2 lg:w-[20vw] md:w-[20vw] w-full flex flex-col mb-2">
      <div className="h-4 bg-gray-300 mb-2 rounded"></div>
      <div className="h-4 bg-gray-300 mb-2 rounded"></div>
      <div className="h-4 bg-gray-300 mb-2 rounded"></div>
      <div className="h-4 bg-gray-300 mb-2 rounded"></div>
      <div className="h-4 bg-gray-300 mb-2 rounded"></div>
      <div className="flex lg:h-full md:h-full h-full justify-start items-end">
        <div className="h-10 w-32 bg-gray-300 rounded-lg"></div>
      </div>
    </div>
  </div>
);

export default function Shop() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const url = useLocation();
  const { category, makes, model, year } = useParams();
  
  // Detect navigation type from HomePage
  const skipSearchOnLoad = url.state && url.state.skipSearch;
  
  // Clear the state after reading
  useEffect(() => {
    if (skipSearchOnLoad) {
      navigate(url.pathname, { replace: true, state: {} });
    }
  }, [skipSearchOnLoad, navigate, url.pathname]);

  // Refs for tracking
  const isInitialMount = useRef(true);
  const isFetchingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const hasAutoSearched = useRef(false);
  const hasLoadedDropdowns = useRef(false);

  // State declarations
  const [likedProducts, setLikedProducts] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [engineOptions, setEngineOptions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedMake, setSelectedMake] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedEngine, setSelectedEngine] = useState("");
  const [makeOptions, setMakeOptions] = useState([]);
  const [loadingModel, setLoadingModel] = useState(false);
  const [loadingMake, setLoadingMake] = useState(false);
  const [loadingEngine, setLoadingEngine] = useState(false);
  const [partTypeOptions, setPartTypeOptions] = useState([]);
  const [uniquePartTypeOptions, setUniquePartTypeOptions] = useState([]);
  const [loadingPartType, setLoadingPartType] = useState(false);
  const [selectedPartType, setSelectedPartType] = useState("");
  const [selectedPartTypeLabel, setSelectedPartTypeLabel] = useState("");
  const wishlistData = useSelector((state) => state.Wishlist.WishListProducts);
  const { getYearData: yearOptions } = useSelector((state) => state.MakeYearModel);
  const [loading, setLoading] = useState(false);
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [subPartTypes, setSubPartTypes] = useState([]);
  const [showSubPartTypes, setShowSubPartTypes] = useState(false);
  const [selectedSubPartTypes, setSelectedSubPartTypes] = useState([]);
  const [searchPartNum, setSearchPartNum] = useState("");
  const [partNoLoading, setPartNoLoading] = useState(false);
  const [clearPartNo, setClearPartNo] = useState(false);
  const [tempProducts, setTempProducts] = useState([]);
  const [addedToCartItems, setAddedToCartItems] = useState(new Set());
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [initialProducts, setInitialProducts] = useState([]);
  const [clearTrigger, setClearTrigger] = useState(0);
  const inputRef = useRef(null);
  const [autoSearchAfterRestore, setAutoSearchAfterRestore] = useState(false);
  
  // Track if this is a category-only navigation
  const isCategoryOnlyNavigation = category && !year && !makes && !model;

  // Get products from Redux store
  const getProducts = useSelector((state) => state.product.product);

  const itemsPerPage = 10;

  // PRODUCT DISPLAY AND PRICE FETCHING
  const [allProductsWithPrice, setAllProductsWithPrice] = useState([]);
  const [productsWithPrice, setProductsWithPrice] = useState([]);
  const [dynamicFilter, setDynamicFilter] = useState({
    partNo: [],
    aaiBrandId: []
  })

  // Scroll to top on mount and page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  // Fetch year data once
  useEffect(() => {
    if (yearOptions.length === 0) {
      dispatch(getYearDataAPi());
    }
  }, [dispatch]);

  // Restore filter state on mount (from product detail page)
  useEffect(() => {
    const savedFilters = sessionStorage.getItem("filters");
    const fromDetail = sessionStorage.getItem("filtersFromDetail") === "true";

    if (!fromDetail || skipSearchOnLoad) {
      if (!fromDetail) {
        sessionStorage.removeItem("filters");
      }
      if (skipSearchOnLoad && sessionStorage.getItem("filtersFromDetail")) {
        sessionStorage.removeItem("filtersFromDetail");
      }
      return;
    }

    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        
        setSelectedYear(filters.selectedYear || "");
        setSelectedMake(filters.selectedMake || "");
        setSelectedModel(filters.selectedModel || "");
        setSelectedEngine(filters.selectedEngine || "");
        setSelectedPartType(filters.selectedPartType || "");
        setSelectedPartTypeLabel(filters.selectedPartTypeLabel || "");
        setSelectedProduct(filters.selectedProduct || "");
        setSelectedSubPartTypes(filters.selectedSubPartTypes || []);
        setCurrentPage(filters.currentPage || 1);

        if (filters.selectedYear || filters.selectedMake || filters.selectedModel || filters.selectedPartType) {
          setIsFilterApplied(true);
          setShowSubPartTypes(true);
        }

        setAutoSearchAfterRestore(true);

        const loadOptions = async () => {
          try {
            const [makeRes, modelRes, prodRes] = await Promise.all([
              filters.selectedYear ? getMakeDataAPi(filters.selectedYear) : Promise.resolve([]),
              filters.selectedYear && filters.selectedMake ? getModelDataAPi({
                selectedYear: filters.selectedYear,
                selectedMake: filters.selectedMake,
              }) : Promise.resolve([]),
              filters.selectedYear && filters.selectedMake && filters.selectedModel ? getProductDataAPi({
                selectedYear: filters.selectedYear,
                selectedMake: filters.selectedMake,
                selectedModel: filters.selectedModel,
              }) : Promise.resolve([]),
            ]);
            if (makeRes) setMakeOptions(makeRes);
            if (modelRes) setModelOptions(modelRes);
            if (prodRes) {
              setPartTypeOptions(prodRes);
              const uniqueByValueAndLabel = Array.from(
                new Map(prodRes.map(item => [`${item.value}|||${item.label}`, item])).values()
              ).sort((a, b) => a.label.localeCompare(b.label));
              setUniquePartTypeOptions(uniqueByValueAndLabel || []);
            }
            if (filters.selectedPartType) {
              const subParts = (prodRes || []).filter(opt => opt.value == filters.selectedPartType && opt.label !== filters.selectedPartType);
              const mainPartType = { value: filters.selectedPartType, label: filters.selectedPartType };
              setSubPartTypes([mainPartType, ...subParts]);
            }
          } catch (err) {
            console.error("Error loading restored dropdown options:", err);
          }
        };

        loadOptions();

        setTimeout(() => {
          if (filters.selectedYear && filters.selectedMake && filters.selectedModel && filters.selectedPartType) {
            dispatch(
              getPartListDataAPi({
                selectedYear: filters.selectedYear,
                selectedMake: filters.selectedMake,
                selectedModel: filters.selectedModel,
                selectedEngine: filters.selectedEngine,
                selectedPartType: filters.selectedPartType,
              })
            );
          }
        }, 300);

      } catch (error) {
        console.error("Error restoring saved filters:", error);
      }
    }
  }, []);

  // Helper function to check if filters changed
  const haveFiltersChanged = useCallback(() => {
    const currentParams = { category, makes, model, year };
    const prevParams = JSON.parse(sessionStorage.getItem('prevParams') || '{}');

    const changed = JSON.stringify(currentParams) !== JSON.stringify(prevParams);

    if (changed) {
      sessionStorage.setItem('prevParams', JSON.stringify(currentParams));
    }

    return changed;
  }, [category, makes, model, year]);

  // Initialize products from Redux store
  useEffect(() => {
    if (!getProducts || getProducts.length === 0) {
      if (!forceRefresh) {
        setAllProductsWithPrice([]);
        setProductsWithPrice([]);
      }
      return;
    }

    const partNos = getProducts.map(item => item?.part_no);
    const brandIds = getProducts.map(item => item?.aaiabrandid);

    setDynamicFilter(prev => ({
      ...prev,
      partNo: partNos,
      aaiBrandId: brandIds
    }));

    const fetchDynamicProductsWithPrice = async () => {
      try {
        const res = await getDynamicProductList({
          tableName: "productPricing",
          operation: "records",
          filters: {
            partNo: partNos,
            aaiBrandId: brandIds
          }
        });

        if (res?.success) {
          const pricingData = res.data;

          const mergedProducts = getProducts.map(product => {
            const matched = pricingData?.find(p => {
              const brandList = p.aaiBrandId
                ?.split(",")
                .map(b => b.trim());

              const brandMatch = brandList?.includes(
                product.aaiabrandid?.toString().trim()
              );

              const partMatch =
                p.partNo?.toString().trim().toLowerCase() ===
                product.part_no?.toString().trim().toLowerCase();

              return partMatch && brandMatch;
            });

            return {
              ...product,
              sellingPrice: matched?.sellingPrice ?? 0,
              height: "",
              width: "",
              length: "",
              weight: "",
            };
          });

          setAllProductsWithPrice(mergedProducts);
          setProductsWithPrice(isSearchMode ? [] : mergedProducts);
        }
      } catch (err) {
        console.error("Dynamic product fetch failed:", err);
      }
    };

    fetchDynamicProductsWithPrice();

    const initializedProducts = getProducts.map(product => ({
      ...product,
      uniqueKey: product.part_no + product.aaiabrandid,
      sellingPrice: 0,
      height: "",
      width: "",
      length: "",
      weight: "",
    }));

    if (!isSearchMode && !isFilterApplied) {
      setInitialProducts(initializedProducts);
    }

    setCurrentPage(1);
    setTempProducts(initializedProducts);

  }, [getProducts, isSearchMode, isFilterApplied, forceRefresh]);

  // Handle clear filters trigger
  useEffect(() => {
    if (clearTrigger > 0) {
      setAllProductsWithPrice([]);
      setProductsWithPrice([]);
      setIsSearchMode(false);
      setSearchPartNum("");
      setClearPartNo(false);
      setCurrentPage(1);
      setLoading(true);
      dispatch(addFilteredProducts([]));

      const fetchDefaultProducts = async () => {
        try {
          const result = await dispatch(
            getPartListDataAPi({
              selectedYear: "2024",
              selectedMake: "0009",
              selectedModel: "1418",
            })
          );

          if (result.payload?.success) {
            console.log('Default products fetched successfully');
          } else {
            console.error('API call failed:', result.payload);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching default products:', error);
          setLoading(false);
        }
      };

      fetchDefaultProducts();
    }
  }, [clearTrigger, dispatch]);

  // MAIN PRODUCT FETCHING - Handles both scenarios
  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const fetchProduct = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      const savedFilters = sessionStorage.getItem("filters");
      const urlParams = new URLSearchParams(url.search);
      const partNumber = urlParams.get('partNumber');

      try {
        setLoading(true);

        // Handle part number search from URL
        if (partNumber && !skipSearchOnLoad) {
          setSearchPartNum(partNumber);
          setPartNoLoading(true);
          try {
            const res = await getPartsByPartNo(partNumber);
            if (res.data.success && res.data.parts.length > 0) {
              setClearPartNo(true);
              dispatch(addFilteredProducts(res.data.parts));
              toast.success(`Found ${res.data.parts.length} product(s)`);
            } else {
              toast.error("No Data found!");
            }
          } catch (err) {
            if (err.name !== 'AbortError') {
              toast.error(err?.response?.data?.message || err?.message || "Error occurred!");
            }
          } finally {
            setPartNoLoading(false);
            setLoading(false);
          }
          isFetchingRef.current = false;
          return;
        }

        // On initial mount or when URL params change
        if (isInitialMount.current || haveFiltersChanged()) {
          isInitialMount.current = false;

          // Reset auto-search flag when URL params change
          if (haveFiltersChanged()) {
            hasAutoSearched.current = false;
            hasLoadedDropdowns.current = false;
          }

          if (skipSearchOnLoad) {
            setIsFilterApplied(false);
          }

          // CASE 1: Category-only navigation (from Featured Categories/Services)
          if (isCategoryOnlyNavigation) {
            console.log("✅ Category-only navigation detected:", { category });
            
            // Auto-select the category in Part Type dropdown
            setSelectedPartType(category);
            setSelectedPartTypeLabel(category);
            setSelectedEngine("All~~~");
            
            // Set default year, make, model for category search
            setSelectedYear("2024");
            setSelectedMake("0009");
            setSelectedModel("1418");
            
            // Fetch engine options for this category
            try {
              setLoadingEngine(true);
              const engineRes = await getEngineDataAPi({
                selectedYear: "2024",
                selectedMake: "0009",
                selectedModel: "1418",
                selectedCategory: category
              });
              if (engineRes && engineRes.length > 0) {
                setEngineOptions(engineRes);
                console.log("✅ Engine options loaded for category:", engineRes.length);
              }
            } catch (err) {
              console.error("Error fetching engine options for category:", err);
            } finally {
              setLoadingEngine(false);
            }
            
            // Auto-fetch products for this category
            if (!skipSearchOnLoad && !hasAutoSearched.current) {
              hasAutoSearched.current = true;
              
              try {
                console.log("🔄 Auto-fetching products for category:", category);
                const resp = await dispatch(
                  getPartListDataAPi({
                    selectedYear: "2024",
                    selectedMake: "0009",
                    selectedModel: "1418",
                    selectedEngine: "All~~~",
                    selectedPartType: category,
                  })
                );
                setIsFilterApplied(true);
                setShowSubPartTypes(true);
                console.log("✅ Category products fetched successfully");
              } catch (err) {
                console.error("Error fetching category products:", err);
              }
            }
          }
          
          // CASE 2: Full filter navigation (year, make, model from "Select Your Vehicle")
          else if (year && makes && model) {
            console.log("✅ Full filter navigation detected:", { year, makes, model, category });
            
            // Set states but DON'T auto-fetch (wait for user to click Search)
            setSelectedYear(year);
            setSelectedMake(makes);
            setSelectedModel(model);
            setSelectedEngine("All~~~");
            
            // Populate make options
            if (!hasLoadedDropdowns.current) {
              try {
                const makeRes = await getMakeDataAPi(year);
                if (makeRes) setMakeOptions(makeRes);
              } catch (err) {
                console.error("Error fetching make options:", err);
              }
              
              // Populate model options
              try {
                const modelRes = await getModelDataAPi({ selectedYear: year, selectedMake: makes });
                if (modelRes) setModelOptions(modelRes);
              } catch (err) {
                console.error("Error fetching model options:", err);
              }
              
              // Populate part type options
              try {
                const productRes = await getProductDataAPi({
                  selectedYear: year,
                  selectedMake: makes,
                  selectedModel: model
                });
                if (productRes) {
                  setPartTypeOptions(productRes);
                  const uniqueByValueAndLabel = Array.from(
                    new Map(productRes.map(item => [`${item.value}|||${item.label}`, item])).values()
                  ).sort((a, b) => a.label.localeCompare(b.label));
                  setUniquePartTypeOptions(uniqueByValueAndLabel || []);
                  
                  // If category is also present, auto-select it
                  if (category) {
                    const matchedOption = uniqueByValueAndLabel.find(opt => opt.value == category);
                    if (matchedOption) {
                      setSelectedPartType(category);
                      setSelectedPartTypeLabel(matchedOption.label);
                      
                      // Fetch engine options for this category
                      try {
                        setLoadingEngine(true);
                        const engineRes = await getEngineDataAPi({
                          selectedYear: year,
                          selectedMake: makes,
                          selectedModel: model,
                          selectedCategory: category
                        });
                        if (engineRes) {
                          setEngineOptions(engineRes);
                        }
                      } catch (err) {
                        console.error("Error fetching engine options:", err);
                      } finally {
                        setLoadingEngine(false);
                      }
                    }
                  }
                }
              } catch (err) {
                console.error("Error fetching product options:", err);
              }
              
              hasLoadedDropdowns.current = true;
            }
            
            // Auto-fetch only if category is present and we haven't auto-searched yet
            if (category && !skipSearchOnLoad && !hasAutoSearched.current) {
              hasAutoSearched.current = true;
              
              try {
                console.log("🔄 Auto-fetching products for category with filters:", category);
                const resp = await dispatch(
                  getPartListDataAPi({
                    selectedYear: year,
                    selectedMake: makes,
                    selectedModel: model,
                    selectedEngine: "All~~~",
                    selectedPartType: category,
                  })
                );
                setIsFilterApplied(true);
                setShowSubPartTypes(true);
              } catch (err) {
                console.error("Error fetching filtered products:", err);
              }
            }
          }
          
          // CASE 3: No params or just category without full filters
          else if (category && !year && !makes && !model) {
            // This is handled in CASE 1 above
          }
          
          // Default: Fetch default products if no params
          else if (!category && !year && !makes && !model && !savedFilters) {
            console.log("No params, loading default products");
            await dispatch(
              getPartListDataAPi({
                selectedYear: "2024",
                selectedMake: "0009",
                selectedModel: "1418",
              })
            );
          }

          // Fetch wishlist products (non-blocking)
          const wishlistResponse = await dispatch(getWishlistProducts());
          if (wishlistResponse.payload) {
            setLikedProducts(
              wishlistResponse.payload.data.wishlist.map((item) => item.partKey)
            );
          }
          
          isFetchingRef.current = false;
          setLoading(false);
          return;
        }

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching product data:", error);
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
          isFetchingRef.current = false;
        }
      }
    };

    fetchProduct();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [category, makes, model, year, url.search, haveFiltersChanged]);

  // Handle filter changes
  const handleYearChange = async (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    setSelectedMake("");
    setSelectedModel("");
    setSelectedPartType("");
    setSelectedPartTypeLabel("");
    setSelectedEngine("");

    try {
      setLoadingMake(true);
      const res = await getMakeDataAPi(year);
      if (res) {
        setMakeOptions(res);
      }
    } catch (error) {
      console.error("Error in fetching make list:", error);
    } finally {
      setLoadingMake(false);
    }
  };

  const handleMakeChange = async (event, year) => {
    const selectedMake = event.target.value;
    setSelectedMake(selectedMake);
    setSelectedModel("");
    setSelectedPartType("");
    setSelectedPartTypeLabel("");
    setSelectedEngine("");

    setLoadingModel(true);
    try {
      const res = await getModelDataAPi({ selectedYear: year || selectedYear, selectedMake });
      if (res) {
        setModelOptions(res);
      }
    } catch (error) {
      console.error("Error fetching model data:", error);
    } finally {
      setLoadingModel(false);
    }
  };

  const handleModelChange = async (event, year, make) => {
    const selectedModel = event.target.value;
    setSelectedModel(selectedModel);
    setSelectedPartType("");
    setSelectedPartTypeLabel("");
    setSelectedEngine("");

    try {
      setLoadingPartType(true);
      const res = await getProductDataAPi({
        selectedYear: year || selectedYear,
        selectedMake: make || selectedMake,
        selectedModel: selectedModel
      });
      if (res) {
        setPartTypeOptions(res);
        const uniqueByValueAndLabel = Array.from(
          new Map(res.map(item => [`${item.value}|||${item.label}`, item])).values()
        ).sort((a, b) => a.label.localeCompare(b.label));
        setUniquePartTypeOptions(uniqueByValueAndLabel || []);
      }
    } catch (error) {
      console.error("Error in fetching product data:", error);
    } finally {
      setLoadingPartType(false);
    }
  };

  const handlePartTypeChange = async (e, year, make, model) => {
    const selectedComposite = e.target.value;
    
    if (!selectedComposite) {
      setSelectedPartType("");
      setSelectedPartTypeLabel("");
      setSubPartTypes([]);
      setSelectedSubPartTypes([]);
      return;
    }
    
    const [partType, partName] = selectedComposite.split("|||");
    
    setSelectedPartType(partType);
    setSelectedPartTypeLabel(partName);
    setSelectedEngine("");

    try {
      const allVariantsForThisType = (partTypeOptions || []).filter((option) => {
        return String(option.value) === String(partType);
      });
      
      const mainPartType = { value: partType, label: partName };
      const subPartTypes = allVariantsForThisType.filter(
        variant => variant.label !== partName
      );
      
      setSubPartTypes([mainPartType, ...subPartTypes]);
      setSelectedSubPartTypes([mainPartType]);

      setLoadingEngine(true);
      const res = await getEngineDataAPi({
        selectedYear: year || selectedYear,
        selectedMake: make || selectedMake,
        selectedModel: model || selectedModel,
        selectedCategory: partType
      });
      if (res) {
        setEngineOptions(res);
      }
    } catch (err) {
      console.error("Error in fetching engine list:", err);
    } finally {
      setLoadingEngine(false);
    }
  };

  const handleEngineChange = async (event) => {
    const selectedEngine = event.target.value;
    setSelectedEngine(selectedEngine);
  };

  // Clear filters function
  const clearFilters = () => {
    setShowSubPartTypes(false);
    sessionStorage.removeItem("filters");
    sessionStorage.removeItem("filtersFromDetail");
    setIsFilterApplied(false);
    setSelectedSubPartTypes([]);
    setSubPartTypes([]);
    setSelectedMake("");
    setSelectedModel("");
    setSelectedYear("");
    setCurrentPage(1);
    setSelectedProduct("");
    setSelectedEngine("");
    setSelectedPartType("");
    setSelectedPartTypeLabel("");
    setPartTypeOptions([]);
    setModelOptions([]);
    setMakeOptions([]);
    setEngineOptions([]);
    setIsSearchMode(false);
    setSearchPartNum("");
    setClearPartNo(false);
    sessionStorage.removeItem('prevParams');
    setAllProductsWithPrice([]);
    setProductsWithPrice([]);
    setLoading(true);
    dispatch(
      getPartListDataAPi({
        selectedYear: "2024",
        selectedMake: "0009",
        selectedModel: "1418",
      })
    ).finally(() => {
      setLoading(false);
    });
  };

  // Search filter handler (called when user clicks Search button)
  const handelmakeyearmodel = async (e) => {
    e && e.preventDefault();
    
    if (!selectedPartType) {
      toast.error("Please select a Part Type first");
      return;
    }
    
    setLoading(true);
    setShowSubPartTypes(true);
    setTempProducts(getProducts);

    if (selectedPartType && !selectedSubPartTypes.some(s => s.value === selectedPartType)) {
      const main = { value: selectedPartType, label: selectedPartTypeLabel || selectedPartType };
      setSelectedSubPartTypes(prev => [main, ...prev]);
    }

    try {
      const resp = await dispatch(
        getPartListDataAPi({
          selectedYear: selectedYear,
          selectedMake: selectedMake,
          selectedModel: selectedModel,
          selectedEngine: selectedEngine,
          selectedPartType,
        })
      );
      toast.success("Products fetched successfully");
    } catch (error) {
      console.error("Error in search filter:", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
      setIsFilterApplied(true);
    }
  };

  // Part number search handler
  const handlePartNumSearch = () => {
    if (!searchPartNum.trim()) return;

    setPartNoLoading(true);

    getPartsByPartNo(searchPartNum)
      .then((res) => {
        if (res.data.success && res.data.parts.length > 0) {
          setClearPartNo(true);
          dispatch(addFilteredProducts(res.data.parts));
          const initializedProducts = res.data.parts.map(product => ({
            ...product,
            uniqueKey: product.part_no + product.aaiabrandid,
            sellingPrice: 0,
            height: "",
            width: "",
            length: "",
            weight: "",
          }));
          setAllProductsWithPrice(initializedProducts);
          setProductsWithPrice(initializedProducts);
          setIsSearchMode(true);
          setIsFilterApplied(true);
          toast.success(`Found ${res.data.parts.length} product(s)`);
        } else {
          toast.error("No Data found!");
          setAllProductsWithPrice([]);
          setProductsWithPrice([]);
          dispatch(addFilteredProducts([]));
          setIsSearchMode(false);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || "Error occurred!");
        setAllProductsWithPrice([]);
        setProductsWithPrice([]);
        dispatch(addFilteredProducts([]));
        setIsSearchMode(false);
      })
      .finally(() => {
        setPartNoLoading(false);
      });
  };
  
  const handleClearPartNo = () => {
    setSearchPartNum("");
    setClearPartNo(false);
    setIsSearchMode(false);
    setAllProductsWithPrice([]);
    setProductsWithPrice([]);
    setLoading(true);
    dispatch(
      getPartListDataAPi({
        selectedYear: "2024",
        selectedMake: "0009",
        selectedModel: "1418",
      })
    ).finally(() => {
      setLoading(false);
    });
  };

  // Wishlist functions
  const toggleLike = async (product) => {
    if (!localStorage.getItem("customerId")) {
      localStorage.setItem("redirect", url.pathname);
      navigate("/login");
      return;
    }
  
    const partKey = product.part_key;
  
    setLikedProducts((prev) =>
      prev.includes(partKey)
        ? prev.filter((id) => id !== partKey)
        : [...prev, partKey]
    );
  
    if (!likedProducts.includes(partKey)) {
      await addToWishList(product);   
    } else {
      await deleteFromWishList(product);
    }
  };

  const addToWishList = async (product) => {
    const wishlistdata = {
      customerId: localStorage.getItem("customerId"),
      partKey: product.part_key,
      uniqueSerialNo: product.part_no + product.aaiabrandid, 
      price: product.sellingPrice ?? 0,
      part_no: product.part_no,
      brand: product.brand || '',
      supplier: product.supplier || '',
      part_type: product.part_type || '',
      image: product.image || '',
    };
  
    try {
      const response = await dispatch(addToWishlist(wishlistdata));
           dispatch(getWishlistCount());

      if (response.payload?.data?.success) {
        toast.success("Product added to wishlist successfully");
      }
    } catch (error) {
      console.error("Error adding product to wishlist:", error);
    }
  };

  const deleteFromWishList = async (product) => {
    const payload = {
      customerId: localStorage.getItem("customerId"),
      partkey: product.part_key,
    };
  
    try {
      const response = await dispatch(deleteFromWishlist(payload));
      dispatch(getWishlistCount());

      if (response.payload?.data?.success) {
        toast.success("Product removed from wishlist successfully");
      }
    } catch (error) {
      console.error("Error removing product from wishlist:", error);
    }
  };

  const handleNavigateSinglePage = (item) => {
    if (isFilterApplied) {
      const filters = {
        selectedYear,
        selectedMake,
        selectedModel,
        selectedEngine,
        selectedPartType,
        selectedPartTypeLabel,
        selectedProduct,
        selectedSubPartTypes,
        currentPage,
      };
      sessionStorage.setItem("filters", JSON.stringify(filters));
      sessionStorage.setItem("filtersFromDetail", "true");
    }

    navigate(`/shop/single-product/${item.part_key}`, {
      state: {
        uniqueKey: item.part_no + item.aaiabrandid,
        part_no: item.part_no,
        sellingPrice: item?.sellingPrice || 0,
      }
    });
  };

  const filteredParts = useMemo(() => {
    if (selectedSubPartTypes.length === 0) return allProductsWithPrice;
    const labelsSet = new Set(selectedSubPartTypes.map((f) => f.label?.toUpperCase()));
    return allProductsWithPrice?.filter((p) => labelsSet.has(p.part_type?.toUpperCase()));
  }, [selectedSubPartTypes, allProductsWithPrice]);

  useEffect(() => {
    if (autoSearchAfterRestore) {
      handelmakeyearmodel();
      setAutoSearchAfterRestore(false);
    }
  }, [autoSearchAfterRestore]);

  const currentItems = useMemo(() => {
    const productsArray = Array.isArray(filteredParts) ? filteredParts : [];
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return productsArray.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredParts, currentPage, itemsPerPage]);

  // Fetch prices for current page items
  useEffect(() => {
    if (!currentItems || currentItems.length === 0 || isFetchingPrices) {
      return;
    }

    const fetchPricesForItems = async () => {
      setIsFetchingPrices(true);

      try {
        const pricePromises = currentItems.map(async (product) => {
          const partKey = product.part_no + product.aaiabrandid;
          try {
            const res = await dispatch(getProductPriceByUniceId(partKey));
            const priceData = res.payload?.data?.productPricingresp[0] || {};
            return {
              ...product,
              uniqueKey: partKey,
              sellingPrice: priceData.sellingPrice || 0,
              height: priceData.height || "",
              width: priceData.width || "",
              length: priceData.length || "",
              weight: priceData.weight || "",
            };
          } catch (err) {
            return {
              ...product,
              uniqueKey: partKey,
              sellingPrice: 0,
              height: "",
              width: "",
              length: "",
              weight: "",
            };
          }
        });

        const priceResults = await Promise.all(pricePromises);

        setAllProductsWithPrice(prev => {
          const updated = [...prev];
          priceResults.forEach(newProduct => {
            const matchingIndices = updated.reduce((indices, product, index) => {
              if (product.uniqueKey === newProduct.uniqueKey) {
                indices.push(index);
              }
              return indices;
            }, []);

            matchingIndices.forEach(index => {
              updated[index] = { ...updated[index], ...newProduct };
            });
          });
          return updated;
        });

        setProductsWithPrice(priceResults);
      } catch (err) {
        console.error("Error in fetching prices:", err);
        setProductsWithPrice(currentItems);
      } finally {
        setIsFetchingPrices(false);
      }
    };

    fetchPricesForItems();
  }, [currentItems, dispatch]);

  useEffect(() => {
    if (!isFetchingPrices && currentItems && currentItems.length > 0) {
      setProductsWithPrice(currentItems);
    }
  }, [currentItems, isFetchingPrices]);

  // Pagination handlers
  const nextPage = () => {
    if (currentPage * itemsPerPage < (filteredParts?.length || 0)) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  // Add to cart handler
  const handleAddToCart = async (item) => {
    if (!localStorage.getItem("customerId")) {
      localStorage.setItem("redirect", url.pathname);
      navigate("/login");
      return;
    }

    try {
      await AddToCart({ ...item, uniqueKey: item.part_no + item.aaiabrandid });
      toast.success("Product added to cart successfully");
      setAddedToCartItems(prev => new Set([...prev, item.part_key]));
      dispatch(cartProductCount(localStorage.getItem("customerId")));
    } catch (error) {
      toast.error("Failed to add product to cart");
    }
  };

  const totalPages = Math.ceil((filteredParts?.length || 0) / itemsPerPage);

  return (
    <div className="mt-[106px]">
      <div className="flex lg:pt-2 md:pt-2 justify-center">
        <div className="w-full">
          <div className="flex flex-col">
            <div className="bg-blue-900 p-3 flex justify-center items-center gap-2">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const trimmedValue = searchPartNum.trim();
                  if (trimmedValue) {
                    handlePartNumSearch();
                  }
                }}
                className="flex items-center rounded-full border border-gray-400 bg-white ps-3 pe-2 lg:w-1/3 w-full relative"
              >
                <IoSearch size="25px" color="orange" />
                <input
                  ref={inputRef}
                  value={searchPartNum}
                  onChange={(e) => setSearchPartNum(e.target.value)}
                  type="text"
                  placeholder="Search Part Number"
                  className="px-1 py-2 bg-transparent text-gray-500 focus:outline-none w-full"
                />
                <button type="submit" style={{ display: 'none' }}></button>
                {partNoLoading && <Spinner size="sm" className="me-2" />}
                {clearPartNo && (
                  <X
                    onClick={handleClearPartNo}
                    className="rounded-full me-1 cursor-pointer"
                    color="blue"
                  />
                )}
              </form>
              {searchPartNum !== "" && (
                <div
                  onClick={handlePartNumSearch}
                  className="rounded-pill bg-white border p-2.5 cursor-pointer"
                >
                  <IoSearch
                    size="22px"
                    color={partNoLoading ? "gray" : "blue"}
                  />
                </div>
              )}
            </div>

            <div className="bg-transparent drop-shadow-md py-4 px-2 m-2 border-2 border-gray-200 rounded-xl">
              <div className="flex flex-col lg:flex-row gap-2 items-center">
                <div className="flex-1 w-full lg:w-auto">
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                    <select
                      value={selectedYear}
                      onChange={handleYearChange}
                      className={`border-2 border-blue-900 p-2 my-1 rounded w-full ${isFilterApplied && !isCategoryOnlyNavigation ? 'bg-gray-200 cursor-not-allowed opacity-60' : ''}`}
                      disabled={isFilterApplied && !isCategoryOnlyNavigation}
                    >
                      <option value="">Year</option>
                      {yearOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="relative">
                      <select
                        value={selectedMake}
                        onChange={(e) => handleMakeChange(e, selectedYear)}
                        className={`border-2 border-blue-900 p-2 my-1 rounded w-full pr-8 ${!selectedYear || (isFilterApplied && !isCategoryOnlyNavigation) || loadingMake ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                        disabled={!selectedYear || (isFilterApplied && !isCategoryOnlyNavigation) || loadingMake}
                      >
                        <option value="">
                          {loadingMake ? "Loading..." : "Make"}
                        </option>
                        {(makeOptions || []).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {loadingMake && (
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <Spinner size="sm" color="blue.500" />
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <select
                        value={selectedModel}
                        onChange={(e) => handleModelChange(e, selectedYear, selectedMake)}
                        className={`border-2 border-blue-900 p-2 my-1 rounded w-full pr-8 ${!selectedMake || (isFilterApplied && !isCategoryOnlyNavigation) || loadingModel ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                        disabled={!selectedMake || (isFilterApplied && !isCategoryOnlyNavigation) || loadingModel}
                      >
                        <option value="">
                          {loadingModel ? "Loading..." : "Model"}
                        </option>
                        {(modelOptions || [])?.map((option) => (
                          <option key={option?.value} value={option?.value}>
                            {option?.label}
                          </option>
                        ))}
                      </select>
                      {loadingModel && (
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <Spinner size="sm" color="blue.500" />
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <select
                        value={selectedPartType && selectedPartTypeLabel ? `${selectedPartType}|||${selectedPartTypeLabel}` : ""}
                        onChange={(e) => {
                          handlePartTypeChange(e, selectedYear, selectedMake, selectedModel);
                        }}
                        className={`border-2 border-blue-900 p-2 my-1 rounded w-full pr-8 ${(!selectedModel && !isCategoryOnlyNavigation) || (isFilterApplied && !isCategoryOnlyNavigation) || loadingPartType ? 'bg-gray-100 ' : ''}`}
                        disabled={(!selectedModel && !isCategoryOnlyNavigation) || (isFilterApplied && !isCategoryOnlyNavigation) || loadingPartType}
                      >
                        <option value="">{loadingPartType ? "Loading..." : "Part Type"}</option>
                        {(uniquePartTypeOptions || [])?.map((option, idx) => (
                          <option key={`${option.value}-${option.label}-${idx}`} value={`${option.value}|||${option.label}`}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {loadingPartType && (
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <Spinner size="sm" color="blue.500" />
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <select
                        className={`border-2 border-blue-900 p-2 my-1 rounded w-full pr-8 ${(!selectedPartType && !isCategoryOnlyNavigation) || (isFilterApplied && !isCategoryOnlyNavigation) || loadingEngine ? 'bg-gray-100 ' : ''}`}
                        value={selectedEngine}
                        onChange={handleEngineChange}
                        disabled={(!selectedPartType && !isCategoryOnlyNavigation) || (isFilterApplied && !isCategoryOnlyNavigation) || loadingEngine}
                      >
                        <option value="">
                          {loadingEngine ? "Loading..." : "Engine Part"}
                        </option>
                        {(engineOptions || [])?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {loadingEngine && (
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <Spinner size="sm" color="blue.500" />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        colorScheme="orange"
                        className="lg:w-full mt-1"
                        onClick={handelmakeyearmodel}
                        isDisabled={!selectedEngine && !isCategoryOnlyNavigation}
                      >
                        Search
                      </Button>
                      <Button
                        colorScheme="orange"
                        className="lg:w-full mt-1"
                        onClick={clearFilters}
                        isDisabled={!isFilterApplied}
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="">
              {loading ? (
                <div className="relative w-full h-[300px]">
                  <Shoploader />
                </div>
              ) : (
                <div className={`${showSubPartTypes ? 'w-[100%]' : 'w-[90%] mx-auto'} grid gap-2 py-1 px-2 justify-center ${showSubPartTypes ? 'grid-cols-1 md:grid-cols-[20%_80%]' : 'grid-cols-1'}`}>
                  <div className="d-none d-sm-block">
                    {showSubPartTypes && (
                      <CheckboxSelect
                        options={subPartTypes}
                        selectedOptions={selectedSubPartTypes}
                        setSelectedOptions={setSelectedSubPartTypes}
                      />
                    )}
                  </div>
                  <div className="">
                    {productsWithPrice?.length > 0 ? (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-3 justify-center">
                          {[...productsWithPrice]
                            .sort((a, b) =>
                              Number(Number(b.sellingPrice) > 0) -
                              Number(Number(a.sellingPrice) > 0)
                            ).map((item, idx) => (
                              <div
                                className="product-card flex flex-col md:flex-row border-2 min-h-10 shadow-lg rounded-md md:hover:border-blue-400 md:hover:scale-[102.5%] transition-all duration-300 ease-in-out bg-white"
                                key={idx}
                              >
                                <div className="lg:w-[20vw] md:w-[20vw] w-full flex-shrink-0">
                                  <div className="flex justify-between items-start">
                                    <div className="flex space-x-1">
                                      {item.tags?.map((tag) => (
                                        <span
                                          key={tag}
                                          className={`text-xs font-bold px-2 py-1 rounded ${tag === "Sale"
                                            ? "bg-yellow-500 text-white"
                                            : tag === "Popular"
                                              ? "bg-blue-900 text-white"
                                              : "bg-green-500 text-white"
                                            }`}
                                        >
                                          {tag}
                                        </span>
                                      ))}
                                    </div>
                                    <FaHeart
                                      size="25px"
                                      className={`heart-icon cursor-pointer z-1 stroke-2 ${likedProducts.includes(item.part_key)
                                        ? "text-orange-500 fill-orange-500"
                                        : "text-blue fill-blue"
                                        }`}
                                      onClick={() => toggleLike(item)}
                                    />
                                  </div>
                                  <img
                                    src={item.image}
                                    alt={item.part_type}
                                    onClick={() => handleNavigateSinglePage(item)}
                                    className="w-full lg:h-[25vh] md:h-[20vh] h-[26vh] object-contain p-2 hover:scale-90 transition-all duration-300 ease-in-out cursor-pointer rounded-t-md"
                                  />
                                  <p
                                    className="font-semibold min-h-10 truncate px-2 lg:text-base cursor-pointer text-2xl text-start overflow-hidden mb-0"
                                    onClick={() => handleNavigateSinglePage(item)}
                                  >
                                    {item.part_type}
                                  </p>
                                  <div className="flex items-center mt-1 y-2 mx-2">
                                    <div className="flex space-x-1 -mt-3">
                                      {[...Array(5)].map((_, i) => (
                                        <CiStar key={i} className="text-gray-400" />
                                      ))}
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between mx-2 mb-2 space-y-0">
                                    <div className="text-red-600 font-bold">
                                      {`$${Number(item.sellingPrice).toFixed(2)}`}
                                    </div>
                                  </div>
                                </div>
                                <div className="p-2 w-full md:w-[20vw] lg:w-[20vw] flex flex-col">
                                  <p className="mb-[1px]">
                                    <span className="font-semibold">Supplier :</span>
                                    {item.supplier || "N/A"}
                                  </p>
                                  <p className="mb-1">
                                    <span className="font-semibold">Location :</span>
                                    {item.location || "N/A"}
                                  </p>
                                  <p className="mb-1">
                                    <span className="font-semibold">Brand :</span>
                                    {item.brand || "N/A"}
                                  </p>
                                  <p className="mb-1">
                                    <span className="font-semibold">Part Number :</span>
                                    {item.part_no || "N/A"}
                                  </p>
                                  <p className="mb-1">
                                    <span className="font-semibold">Part Type :</span>
                                    {item.part_type || "N/A"}
                                  </p>
                                  <div className="flex lg:h-full md:h-full h-full justify-start items-end">
                                    <button
                                      onClick={() => handleAddToCart(item)}
                                      className={`text-white ${addedToCartItems.has(item.part_key)
                                        ? "bg-green-500"
                                        : "bg-blue-900"
                                        } lg:p-2 md:px-[0.2rem] md:py-[0.5rem] p-[0.65rem] w-full md:w-full lg:w-auto rounded-lg font-bold flex items-center justify-center`}
                                    >
                                      {addedToCartItems.has(item.part_key) ? (
                                        <>
                                          <BsCartCheckFill size="19px" className="me-2" />
                                          Added to Cart
                                        </>
                                      ) : (
                                        <>
                                          <FaCartPlus size="19px" className="ms-0 me-2" />
                                          Add To Cart
                                        </>
                                      )}
                                      <FaArrowRightLong size="17px" className="mx-2" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>

                        {filteredParts?.length > itemsPerPage && (
                          <div className="flex justify-center items-center my-6 space-x-2">
                            <button
                              onClick={prevPage}
                              disabled={currentPage === 1}
                              className="px-4 py-2 rounded-full text-gray-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <ChevronsLeft size={20} />
                            </button>

                            {currentPage > 2 && (
                              <>
                                <button
                                  onClick={() => setCurrentPage(1)}
                                  className="w-9 h-9 rounded-full text-sm font-medium bg-gray-100 hover:bg-orange-200 text-gray-800 transition"
                                >
                                  1
                                </button>
                                {currentPage > 3 && <span className="text-gray-500">...</span>}
                              </>
                            )}

                            {[currentPage - 1, currentPage, currentPage + 1].map((page) => {
                              if (page < 1 || page > totalPages) return null;
                              return (
                                <button
                                  key={page}
                                  onClick={() => setCurrentPage(page)}
                                  className={`w-9 h-9 rounded-full text-sm font-medium transition ${currentPage === page
                                    ? "bg-orange-600 text-white"
                                    : "bg-gray-100 hover:bg-orange-200 text-gray-800"
                                    }`}
                                >
                                  {page}
                                </button>
                              );
                            })}

                            {currentPage < totalPages - 1 && (
                              <>
                                {currentPage < totalPages - 2 && <span className="text-gray-500">...</span>}
                                <button
                                  onClick={() => setCurrentPage(totalPages)}
                                  className="w-9 h-9 rounded-full text-sm font-medium bg-gray-100 hover:bg-orange-200 text-gray-800 transition"
                                >
                                  {totalPages}
                                </button>
                              </>
                            )}

                            <button
                              onClick={nextPage}
                              disabled={currentPage >= totalPages}
                              className="px-4 py-2 rounded-full text-gray-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <ChevronsRight size={20} />
                            </button>
                          </div>
                        )}
                      </>
                    ) : isFetchingPrices ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-3 justify-center">
                        {Array.from({ length: itemsPerPage }).map((_, index) => (
                          <ProductCardSkeleton key={index} />
                        ))}
                      </div>
                    ) : (
                      <div className="flex justify-center items-center h-64">
                        <h2 className="text-xl text-gray-500">No products found</h2>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}